import { Flex, ReportSectionItemContent } from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import {
  formatDivergenceAmount,
  formatDivergenceRate,
} from '@/features/project/projects/detail/income-and-expenditure/_utils/value-converter';
import { useLocaledAmount } from '@/providers';
import { FALLBACK_TEXT, formatValueByUnit } from '@/utils';
import { useTranslation } from 'react-i18next';
import { LEDGER_BORDER } from '../../../utils';

export type DivergenceInExecutionItem = {
  fiscalYear: number | undefined;
  estimatedBusinessExpense: number | null | undefined;
  businessExpense: number | null | undefined;
  divergenceAmount: number | null | undefined;
  reason: string | null | undefined;
  divergenceRate: number | null | undefined;
};

export const KsFundDivergenceInExecutionItem = ({
  data,
}: {
  data: DivergenceInExecutionItem[];
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;
  return (
    <Flex vertical style={{ width: '100%' }}>
      <ReportSectionItemContent
        label={`${data[0]?.fiscalYear?.toString()}（${t('fiscal-year-two-years-ago')}）`}
        labelStyle={{
          width: 91,
          minWidth: 91,
          padding: '2px 8px',
        }}
        value={
          <div style={{ width: '100%' }}>
            <Flex>
              <ReportSectionItemContent
                wrapperStyle={{ borderBottom: 'none' }}
                value={
                  <ReportSectionItemContent
                    label={`${t('business-expenses')}${t('estimate')}`}
                    value={formatValueByUnit(
                      data[0]?.estimatedBusinessExpense,
                      {
                        operation: 'round',
                        unit,
                        showUnitLabel: false,
                      },
                    )}
                    labelStyle={{
                      flex: 1,
                    }}
                    wrapperStyle={{
                      height: 'auto',
                      borderLeft: 'none',
                    }}
                    valueWrapperStyle={{
                      flex: 1,
                      justifyContent: 'flex-end',
                    }}
                  />
                }
              />
              <ReportSectionItemContent
                wrapperStyle={{ borderBottom: 'none' }}
                value={
                  <ReportSectionItemContent
                    label={t('business-expenses')}
                    value={formatValueByUnit(data[0]?.businessExpense, {
                      operation: 'round',
                      unit,
                      showUnitLabel: false,
                    })}
                    labelStyle={{
                      flex: 1,
                    }}
                    wrapperStyle={{
                      height: 'auto',
                      borderLeft: LEDGER_BORDER,
                    }}
                    valueWrapperStyle={{
                      flex: 1,
                      justifyContent: 'flex-end',
                    }}
                  />
                }
              />
            </Flex>
            <Flex>
              <ReportSectionItemContent
                isLast
                value={
                  <ReportSectionItemContent
                    isLast
                    label={t('divergence-amount')}
                    value={formatDivergenceAmount({
                      divergenceAmount: data[0]?.divergenceAmount,
                      estimatedBusinessExpense:
                        data[0]?.estimatedBusinessExpense,
                      businessExpense: data[0]?.businessExpense,
                      isDebugMode,
                    })}
                    labelStyle={{
                      flex: 1,
                    }}
                    wrapperStyle={{
                      height: 'auto',
                      borderLeft: 'none',
                    }}
                    valueWrapperStyle={{
                      flex: 1,
                      justifyContent: 'flex-end',
                    }}
                  />
                }
              />
              <ReportSectionItemContent
                isLast
                value={
                  <ReportSectionItemContent
                    isLast
                    label={t('divergence-rate')}
                    value={formatDivergenceRate(data[0]?.divergenceRate)}
                    labelStyle={{
                      flex: 1,
                    }}
                    wrapperStyle={{
                      height: 'auto',
                      borderLeft: LEDGER_BORDER,
                    }}
                    valueWrapperStyle={{
                      flex: 1,
                      justifyContent: 'flex-end',
                    }}
                  />
                }
              />
            </Flex>
          </div>
        }
      />
      <ReportSectionItemContent
        value={`【${t('reason-for-divergence')}】\n${data[0]?.reason ?? FALLBACK_TEXT}`}
      />

      <ReportSectionItemContent
        label={`${data[1]?.fiscalYear?.toString()}（${t('fiscal-year-one-year-ago')}）`}
        labelStyle={{
          width: 91,
          minWidth: 91,
        }}
        value={
          <div style={{ width: '100%' }}>
            <Flex>
              <ReportSectionItemContent
                isLast
                wrapperStyle={{
                  borderLeft: 'none',
                }}
                value={
                  <ReportSectionItemContent
                    label={`${t('business-expenses')}${t('estimate')}`}
                    value={formatValueByUnit(
                      data[1]?.estimatedBusinessExpense,
                      {
                        operation: 'round',
                        unit,
                        showUnitLabel: false,
                      },
                    )}
                    labelStyle={{
                      flex: 1,
                    }}
                    wrapperStyle={{
                      height: 'auto',
                    }}
                    valueWrapperStyle={{
                      flex: 1,
                      justifyContent: 'flex-end',
                    }}
                  />
                }
              />
              <ReportSectionItemContent
                value={
                  <ReportSectionItemContent
                    isLast
                    label={t('business-expenses')}
                    value={formatValueByUnit(data[1]?.businessExpense, {
                      operation: 'round',
                      unit,
                      showUnitLabel: false,
                    })}
                    labelStyle={{
                      flex: 1,
                    }}
                    wrapperStyle={{
                      height: 'auto',
                      borderLeft: LEDGER_BORDER,
                    }}
                    valueWrapperStyle={{
                      flex: 1,
                      justifyContent: 'flex-end',
                    }}
                  />
                }
              />
            </Flex>
            <Flex>
              <ReportSectionItemContent
                isLast
                wrapperStyle={{
                  borderLeft: 'none',
                }}
                value={
                  <ReportSectionItemContent
                    isLast
                    label={t('divergence-amount')}
                    value={formatDivergenceAmount({
                      divergenceAmount: data[1]?.divergenceAmount,
                      estimatedBusinessExpense:
                        data[1]?.estimatedBusinessExpense,
                      businessExpense: data[1]?.businessExpense,
                      isDebugMode,
                    })}
                    labelStyle={{
                      flex: 1,
                    }}
                    wrapperStyle={{
                      height: 'auto',
                    }}
                    valueWrapperStyle={{
                      flex: 1,
                      justifyContent: 'flex-end',
                    }}
                  />
                }
              />
              <ReportSectionItemContent
                isLast
                value={
                  <ReportSectionItemContent
                    isLast
                    label={t('divergence-rate')}
                    value={formatDivergenceRate(data[1]?.divergenceRate)}
                    labelStyle={{
                      flex: 1,
                    }}
                    wrapperStyle={{
                      height: 'auto',
                      borderLeft: LEDGER_BORDER,
                    }}
                    valueWrapperStyle={{
                      flex: 1,
                      justifyContent: 'flex-end',
                    }}
                  />
                }
              />
            </Flex>
          </div>
        }
      />
      <ReportSectionItemContent
        isLast
        value={`【${t('reason-for-divergence')}】\n${data[1]?.reason ?? FALLBACK_TEXT}`}
      />
    </Flex>
  );
};
